import React, { useState, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { routes } from '@/lib/routes';
import {
  CabinetWorkspaceFrame,
  ScientificGpsBlock,
} from '@/features/cabinet/CabinetWorkspaceFrame';
import { authorApi } from '@/features/workspace/api';
import type {
  AuthorWorkspaceSummary,
  AuthorOverlayKey,
  AuthorProfile,
} from './types';
import { AuthorCabinetGrid } from './components/AuthorCabinetGrid';
import { AuthorCreateHubOverlay } from './components/AuthorCreateHubOverlay';
import { MyRequestsOverlay } from './components/MyRequestsOverlay';
import { SurveysAndFormsOverlay } from './components/SurveysAndFormsOverlay';
import { AuthorCalendarOverlay } from './components/AuthorCalendarOverlay';
import { JointArticlesOverlay } from './components/JointArticlesOverlay';
import { AuthorAIModal } from './components/AuthorModals';
import { RoleAcademicConnectionsOverlay } from '@/features/communications';
import { ResearchToolsPage } from '@/features/research-tools';
import { FileHubPage } from '@/features/file-hub';
import { UsersGroupIcon, RequestFolderIcon } from './components/AuthorIcons';

const OVERLAY_PATHS: Record<AuthorOverlayKey, string> = {
  CREATE_HUB: 'create',
  /** Legacy alias — ENTRY CONTRACT redirects wizard → Create Request Hub. */
  SERVICE_REQUEST_WIZARD: 'create',
  MY_REQUESTS: 'requests',
  SURVEYS_AND_FORMS: 'surveys',
  ACADEMIC_CONNECTIONS: 'connections',
  CALENDAR: 'calendar',
  RESEARCH_TOOLS: 'tools',
  FILES_AND_STORAGE: 'files',
  JOINT_ARTICLES: 'articles',
};

export const AuthorPage: React.FC = () => {
  const { user } = useAuth();
  const { notify } = useToast();
  const navigate = useNavigate();

  const [workspace, setWorkspace] = useState<AuthorWorkspaceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAIModal, setShowAIModal] = useState(false);

  const backToCabinet = () => navigate(routes.author);

  const fetchWorkspace = async () => {
    try {
      setLoading(true);
      const data = await authorApi.workspace();
      setWorkspace(data);
    } catch {
      notify('Не удалось загрузить данные кабинета автора', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkspace();
  }, []);

  const handleProfileUpdated = (updated: AuthorProfile) => {
    if (workspace) {
      setWorkspace({ ...workspace, profile: updated });
    }
  };

  const openOverlay = (key: AuthorOverlayKey) => {
    navigate(OVERLAY_PATHS[key]);
  };

  if (loading && !workspace) {
    return (
      <div className="min-h-screen bg-[#070b14] flex items-center justify-center text-cyan-400">
        <div className="flex flex-col items-center space-y-3">
          <div className="w-10 h-10 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm font-semibold text-slate-300">Загрузка кабинета автора и соавтора...</span>
        </div>
      </div>
    );
  }

  const defaultProfile: AuthorProfile = workspace?.profile || {
    id: 'demo-profile',
    user_id: user?.id || 'demo-user',
    full_name: user?.fullName || 'Жанжигитов Сырым',
    current_role: 'Докторант / Исследователь',
    author_code: 'ZA-AU-2026',
    h_index_scopus: 3,
    h_index_wos: 2,
    completion_percentage: 80,
  };

  const defaultKpis = workspace?.kpis || {
    total_requests: 2,
    in_progress: 2,
    urgent_deadlines: 3,
    unread_messages: 0,
  };

  const cabinetIndex = (
    <CabinetWorkspaceFrame
      links={{
        notebook: routes.authorModule('files'),
        calendar: routes.authorModule('calendar'),
        meetings: routes.authorModule('calendar'),
        connections: routes.authorModule('connections'),
        alliances: routes.authorModule('articles'),
        qa: routes.portalSection('instructions'),
        support: routes.portalSection('contacts'),
      }}
    >
      <div className="space-y-6 text-slate-100">
        <div
          className="role-hero"
          style={{ '--role-accent': '#41d2ff' } as React.CSSProperties}
        >
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-xl font-black text-white shrink-0">
              {defaultProfile.full_name?.charAt(0) || 'С'}
            </div>
            <div className="min-w-0">
              <small>
                {defaultProfile.author_code || 'ZA-AU-2026'} · Автор / соавтор
              </small>
              <h2 className="truncate">{defaultProfile.full_name || 'Сырым Жанжигитов'}</h2>
              <p>
                {defaultProfile.current_role || 'Автор / Соавтор'} ·{' '}
                {defaultProfile.workplace_or_university || 'ЕНУ им. Л.Н. Гумилева'}
              </p>
            </div>
          </div>
          <div className="role-hero-actions">
            <button type="button" className="role-btn" onClick={() => setShowAIModal(true)}>
              AI Консультант
            </button>
            <button type="button" className="role-btn primary" onClick={() => openOverlay('CREATE_HUB')}>
              + Создать заявку
            </button>
          </div>
        </div>

        <div className="owner-hub-top-actions">
          <button type="button" className="owner-hub-ai-action" onClick={() => setShowAIModal(true)}>
            <span className="spark" aria-hidden="true">
              ✦
            </span>
            <span>
              <strong>Научный онлайн-консультант AI</strong>
              <small>Квартили, IMRAD, соавторы и требования журналов</small>
            </span>
            <b>Спросить →</b>
          </button>
          <button type="button" className="owner-hub-create" onClick={() => openOverlay('CREATE_HUB')}>
            + Создать заявку
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 space-y-6">
            <AuthorCabinetGrid
              kpis={defaultKpis}
              profile={defaultProfile}
              onOpenOverlay={openOverlay}
            />
          </div>

          <div className="lg:col-span-4 space-y-5">
            <div className="bg-[#0e1628]/90 border border-slate-800 rounded-3xl p-5 space-y-3 shadow-lg">
              <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-1.5">
                  <RequestFolderIcon className="w-4 h-4 text-cyan-400" />
                  <span>Рабочая очередь заявок</span>
                </h3>
                <button
                  onClick={() => openOverlay('MY_REQUESTS')}
                  className="text-[10px] text-cyan-400 hover:underline"
                >
                  Все ({workspace?.recent_requests?.length || 0})
                </button>
              </div>
              <div className="space-y-2 text-xs">
                {workspace?.recent_requests && workspace.recent_requests.length > 0 ? (
                  workspace.recent_requests.slice(0, 3).map((req) => (
                    <div
                      key={req.id}
                      onClick={() => openOverlay('MY_REQUESTS')}
                      className="p-3 bg-[#090e1a] hover:bg-[#121c33] border border-slate-800 rounded-xl cursor-pointer transition-colors space-y-1"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono font-bold text-cyan-400 text-[11px]">
                          {req.request_number}
                        </span>
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-cyan-950 text-cyan-300 border border-cyan-800">
                          {req.status}
                        </span>
                      </div>
                      <div className="font-semibold text-white truncate">{req.title}</div>
                      <div className="text-[10px] text-slate-500">{req.work_option}</div>
                    </div>
                  ))
                ) : (
                  <div className="text-slate-500 text-[11px] text-center py-2">
                    Нет активных заявок
                  </div>
                )}
              </div>
            </div>

            <div className="bg-[#0e1628]/90 border border-slate-800 rounded-3xl p-5 space-y-3 shadow-lg">
              <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                <h3 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-1.5">
                  <UsersGroupIcon className="w-4 h-4 text-blue-400" />
                  <span>Связи и координация</span>
                </h3>
                <button
                  onClick={() => openOverlay('ACADEMIC_CONNECTIONS')}
                  className="text-[10px] text-blue-400 hover:underline"
                >
                  Диалоги
                </button>
              </div>
              <div className="space-y-2 text-xs">
                {workspace?.connections && workspace.connections.length > 0 ? (
                  workspace.connections.slice(0, 3).map((c) => (
                    <div
                      key={c.id}
                      onClick={() => openOverlay('ACADEMIC_CONNECTIONS')}
                      className="p-2.5 bg-[#090e1a] hover:bg-[#121c33] border border-slate-800 rounded-xl cursor-pointer transition-colors flex items-center justify-between"
                    >
                      <div>
                        <div className="font-semibold text-white">{c.contact_name}</div>
                        <div className="text-[10px] text-blue-400">{c.contact_role}</div>
                      </div>
                      {c.unread_count > 0 && (
                        <span className="w-5 h-5 rounded-full bg-blue-500 text-black text-[10px] font-bold flex items-center justify-center">
                          {c.unread_count}
                        </span>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-slate-500 text-[11px] text-center py-2">
                    Контактов пока нет
                  </div>
                )}
              </div>
            </div>

            <div className="bg-[#0e1628]/90 border border-slate-800 rounded-3xl p-5 space-y-3 shadow-lg">
              <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                <h3 className="text-xs font-bold text-white uppercase tracking-wider">
                  Научные альянсы
                </h3>
                <button
                  onClick={() => openOverlay('JOINT_ARTICLES')}
                  className="text-[10px] text-rose-400 hover:underline"
                >
                  Статьи ({workspace?.joint_articles_count || 0})
                </button>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Участие в коллективных грантовых и публикационных проектах. Доступ к общим ресурсам и базам данных.
              </p>
            </div>
          </div>
        </div>

        <ScientificGpsBlock openTo={routes.authorModule('articles')} />

        {showAIModal && (
          <AuthorAIModal onClose={() => setShowAIModal(false)} />
        )}
      </div>
    </CabinetWorkspaceFrame>
  );

  return (
    <div className="min-h-screen bg-[#070b14] text-slate-100 p-4 md:p-6 role-cabinet-page">
    <Routes>
      <Route index element={cabinetIndex} />

      <Route
        path="create"
        element={
          <AuthorCreateHubOverlay
            onClose={backToCabinet}
          />
        }
      />

      {/* ENTRY CONTRACT: no parallel legacy wizard — always Create Request Hub */}
      <Route path="wizard" element={<Navigate to={routes.authorModule('create')} replace />} />
      <Route path="new" element={<Navigate to={routes.authorModule('create')} replace />} />

      <Route
        path="requests"
        element={
          <MyRequestsOverlay
            onClose={backToCabinet}
            onRequestWizard={() => openOverlay('CREATE_HUB')}
          />
        }
      />

      <Route
        path="surveys"
        element={
          <SurveysAndFormsOverlay
            initialProfile={defaultProfile}
            onClose={backToCabinet}
            onProfileUpdated={handleProfileUpdated}
          />
        }
      />
      <Route
        path="forms"
        element={
          <SurveysAndFormsOverlay
            initialProfile={defaultProfile}
            onClose={backToCabinet}
            onProfileUpdated={handleProfileUpdated}
          />
        }
      />

      <Route
        path="connections"
        element={
          <RoleAcademicConnectionsOverlay
            isOpen
            onClose={backToCabinet}
            roleContext="AUTHOR"
            roleTitle="Автор / соавтор"
          />
        }
      />

      <Route path="calendar" element={<AuthorCalendarOverlay onClose={backToCabinet} />} />

      <Route
        path="tools/*"
        element={<ResearchToolsPage onClose={backToCabinet} onBack={backToCabinet} />}
      />

      <Route
        path="files/*"
        element={<FileHubPage onClose={backToCabinet} onBack={backToCabinet} />}
      />

      <Route path="articles" element={<JointArticlesOverlay onClose={backToCabinet} />} />
    </Routes>
    </div>
  );
};

export default AuthorPage;
